import { createFileRoute } from '@tanstack/react-router'
import { useEffect, useMemo, useState } from 'react'
import { api } from '@/lib/api'
import { getAdminToken } from '@/lib/admin-store'
import {
  applyAdminFilters,
  getAdminNavLinks,
  readAdminFiltersFromSearch,
  type AdminFilters,
} from './-admin-utils'
import styles from './admin.module.css'

export const Route = createFileRoute('/admin/referrals')({
  component: AdminReferralsRoute,
})

function AdminReferralsRoute() {
  const [token] = useState(getAdminToken)
  const [filters, setFilters] = useState<AdminFilters>(readAdminFiltersFromSearch)
  const [rows, setRows] = useState<Awaited<ReturnType<typeof api.adminReferrals>>['items']>([])
  const [summary, setSummary] = useState<{
    pending: number
    qualified: number
    rejected: number
  } | null>(null)
  const [reconcileReason, setReconcileReason] = useState('manual_reconcile')
  const [error, setError] = useState<string | null>(null)
  const [notice, setNotice] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(false)

  const navLinks = useMemo(() => getAdminNavLinks(filters), [filters])
  const referralStatus =
    filters.status === 'pending' || filters.status === 'qualified' || filters.status === 'rejected'
      ? filters.status
      : 'all'

  useEffect(() => {
    const load = async (): Promise<void> => {
      if (!token) {
        return
      }

      setIsLoading(true)
      setError(null)

      try {
        const data = await api.adminReferrals(token, {
          limit: 200,
          ...(referralStatus !== 'all' ? { state: referralStatus } : {}),
        })
        setRows(data.items)
        setSummary(data.summary)
      } catch (loadError) {
        setError(loadError instanceof Error ? loadError.message : 'Failed to load referrals.')
      } finally {
        setIsLoading(false)
      }
    }

    void load()
  }, [referralStatus, token])

  const handleReconcile = async (): Promise<void> => {
    if (!token) {
      return
    }

    setIsLoading(true)
    setError(null)
    setNotice(null)

    try {
      const data = await api.adminReconcileReferrals(token, {
        reason: reconcileReason.trim().length > 0 ? reconcileReason.trim() : 'manual_reconcile',
      })
      setNotice(
        `Reconciled: evaluated ${data.evaluated}, newly qualified ${data.newlyQualified}, pending ${data.stillPending}.`
      )
      const refreshed = await api.adminReferrals(token, {
        limit: 200,
        ...(referralStatus !== 'all' ? { state: referralStatus } : {}),
      })
      setRows(refreshed.items)
      setSummary(refreshed.summary)
    } catch (reconcileError) {
      setError(reconcileError instanceof Error ? reconcileError.message : 'Reconciliation failed.')
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <section className={styles.layout}>
      <header className={styles.hero}>
        <h1 className={styles.title}>Referrals</h1>
        <p className={styles.subtitle}>
          Review attribution states and run referral qualification reconciliation.
        </p>
      </header>

      <section className={styles.card}>
        <div className={styles.tabs}>
          {navLinks.map((link) => (
            <a key={link.path} href={link.href} className={styles.tabLink}>
              {link.label}
            </a>
          ))}
        </div>
        <div className={styles.row}>
          <label>
            State
            <select
              className={styles.select}
              value={referralStatus}
              onChange={(event) =>
                setFilters((current) => ({
                  ...current,
                  status:
                    event.target.value === 'pending' ||
                    event.target.value === 'qualified' ||
                    event.target.value === 'rejected'
                      ? event.target.value
                      : 'all',
                }))
              }
            >
              <option value="all">All</option>
              <option value="pending">Pending</option>
              <option value="qualified">Qualified</option>
              <option value="rejected">Rejected</option>
            </select>
          </label>
          <button
            className={styles.secondaryButton}
            type="button"
            onClick={() => applyAdminFilters(filters)}
          >
            Apply Filters
          </button>
          <button
            className={styles.ghostButton}
            type="button"
            onClick={() =>
              applyAdminFilters({
                ...filters,
                status: 'all',
              })
            }
          >
            Clear Filters
          </button>
        </div>
        <div className={styles.row}>
          <input
            className={styles.input}
            type="text"
            value={reconcileReason}
            onChange={(event) => setReconcileReason(event.target.value)}
            placeholder="Reconcile reason"
          />
          <button
            className={styles.button}
            type="button"
            onClick={() => void handleReconcile()}
            disabled={isLoading || !token}
          >
            Run Reconciliation
          </button>
        </div>
        {notice && <p className={styles.notice}>{notice}</p>}
        {error && <p className={styles.error}>{error}</p>}
      </section>

      {!token && (
        <section className={styles.card}>
          <p className={styles.emptyState}>You don't have admin access.</p>
          <a className={styles.tabLink} href="/admin">
            Go to Home
          </a>
        </section>
      )}

      {token && summary && (
        <section className={styles.card}>
          <div className={styles.grid}>
            <article className={styles.kpi}>
              <p className={styles.kpiLabel}>Pending</p>
              <p className={styles.kpiValue}>{summary.pending}</p>
            </article>
            <article className={styles.kpi}>
              <p className={styles.kpiLabel}>Qualified</p>
              <p className={styles.kpiValue}>{summary.qualified}</p>
            </article>
            <article className={styles.kpi}>
              <p className={styles.kpiLabel}>Rejected</p>
              <p className={styles.kpiValue}>{summary.rejected}</p>
            </article>
          </div>
        </section>
      )}

      {token && !isLoading && rows.length === 0 && !error && (
        <section className={styles.card}>
          <p className={styles.emptyState}>No referral attributions found.</p>
        </section>
      )}

      {token && rows.length > 0 && (
        <section className={styles.card}>
          <h2>Referral Attribution Rows</h2>
          <table className={styles.table}>
            <thead>
              <tr>
                <th>Referrer</th>
                <th>Referred</th>
                <th>Code</th>
                <th>State</th>
                <th>Hold Expires</th>
                <th>Qualified At</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => {
                const search = typeof window !== 'undefined' ? window.location.search : ''
                return (
                  <tr key={row.id}>
                    <td>
                      <a
                        className={styles.tabLink}
                        href={`/admin/users/${encodeURIComponent(row.referrerUserId)}${search}`}
                      >
                        {row.referrerUserId}
                      </a>
                    </td>
                    <td>
                      <a
                        className={styles.tabLink}
                        href={`/admin/users/${encodeURIComponent(row.referredUserId)}${search}`}
                      >
                        {row.referredUserId}
                      </a>
                    </td>
                    <td>{row.referralCode}</td>
                    <td>{row.state}</td>
                    <td>{row.holdExpiresAt ?? '-'}</td>
                    <td>{row.qualifiedAt ?? row.rejectionReason ?? '-'}</td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </section>
      )}
    </section>
  )
}
